import React from 'react';
import { Link } from 'react-router';
import SEO from '../components/SEO';
import GlowCard from '../components/GlowCard';
import AIImage from '../components/AIImage';
import { trackEvent } from '../components/analytics';

const SafetyGovernance: React.FC = () => {
  return (
    <div className="animate-fade-in-up">
      <SEO
        title="Safety Governance Infrastructure for National Aviation Systems"
        description="Unifying SMS, competency, and oversight into a single system of record."
      />

      <section className="py-24 bg-slate-950 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <h1 className="text-5xl lg:text-7xl font-bold font-heading mb-8 leading-tight ">
              Safety Governance Infrastructure for
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent via-white to-brandDeep"> National Aviation Systems</span>
            </h1>
            <p className="text-xl text-slate-400 mb-10 leading-relaxed">
              Unifying SMS, competency, and oversight into a single system of record.
            </p>
            <Link
              to="/contact"
              className="inline-flex px-10 py-4 bg-accent text-primary font-bold rounded-xl"
              onClick={() => trackEvent('demo_request', { form_id: 'safety_governance_hero_overview', module: 'safety' })}
            >
              Request Executive Overview
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 relative p-1 bg-slate-800 rounded-2xl overflow-hidden border border-white/10">
              <AIImage
                prompt="A formal, monochrome executive overview of national safety governance with sealed policy documents and structured records, institutional tone, no UI, minimal lines"
                alt="National safety governance overview"
                className="w-full opacity-95"
                aspectRatio="16:9"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 px-4 border-b border-white/5">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl lg:text-5xl font-bold font-heading mb-8 text-white">When Safety Evidence Is Fragmented</h2>
          <ul className="text-slate-400 text-sm space-y-3">
            <li>SMS managed separately from training</li>
            <li>Findings not linked to competency</li>
            <li>Evidence assembled post-event</li>
            <li>Institutional knowledge lost</li>
          </ul>
        </div>
      </section>

      <section className="py-24 px-4 bg-slate-950/50 border-b border-white/5">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl lg:text-5xl font-bold font-heading mb-8 text-white">
            Governance Model
          </h2>
          <div className="border border-white/10 rounded-2xl p-1 bg-slate-900/40">
            <AIImage
              prompt="Institutional governance diagram on a dark background with centered vertical text lines: 'Authority', then a down arrow, 'SMS (Risk Definition)', then a down arrow, 'LMS (Competency Evidence)', then a down arrow, 'Evidence', then a down arrow, 'One Safety Truth'. Minimal, document-like, no icons, no UI."
              alt="Safety governance model"
              className="w-full opacity-95"
              aspectRatio="16:9"
            />
          </div>
          <p className="text-slate-500 text-sm mt-4 text-center">
            One system of record. Many stakeholders. Authority control preserved by design.
          </p>
          <p className="text-slate-400 text-base leading-relaxed mt-6">
            Safety governance requires authoritative evidence. Risk, competency,
            and evidence must converge into one governed record, with the authority
            retaining verification and decision control at all times.
          </p>
        </div>
      </section>

      <section className="py-24 px-4 bg-slate-950/50 border-b border-white/5">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl lg:text-5xl font-bold font-heading mb-8 text-white">One Safety Truth, Many Stakeholders</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <GlowCard>
              <p className="text-slate-400 text-sm">SMS defines risk</p>
            </GlowCard>
            <GlowCard>
              <p className="text-slate-400 text-sm">LMS enforces competency</p>
            </GlowCard>
            <GlowCard>
              <p className="text-slate-400 text-sm">Authority retains control</p>
            </GlowCard>
            <GlowCard>
              <p className="text-slate-400 text-sm">Operators submit evidence</p>
            </GlowCard>
          </div>
        </div>
      </section>

      <section className="py-24 px-4 border-b border-white/5">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl lg:text-5xl font-bold font-heading mb-8 text-white">Executive Visibility</h2>
          <GlowCard>
          <ul className="text-slate-400 text-sm space-y-3">
            <li><h3 className="text-xl font-bold text-white mb-3">Safety posture overview</h3></li>
            <li><h3 className="text-xl font-bold text-white mb-3">Readiness indicators</h3></li>
            <li><h3 className="text-xl font-bold text-white mb-3">Evidence completeness</h3></li>
            <li><h3 className="text-xl font-bold text-white mb-3">Audit confidence</h3></li>
          </ul></GlowCard>
        </div>
      </section>

      {/* <section className="py-24 px-4 bg-slate-950/50 border-b border-white/5">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl lg:text-5xl font-bold font-heading mb-8 text-white">Explicit Filter</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <GlowCard>
              <h3 className="text-xl font-bold text-white mb-3">Designed for</h3>
              <ul className="text-slate-400 text-sm space-y-2">
                <li>Civil Aviation Authorities</li>
                <li>Air Navigation Service Providers</li>
                <li>National safety oversight bodies</li>
              </ul>
            </GlowCard>
            <GlowCard>
              <h3 className="text-xl font-bold text-white mb-3">Not designed for</h3>
              <ul className="text-slate-400 text-sm space-y-2">
                <li>Individual pilots</li>
                <li>Training schools</li>
                <li>Generic LMS buyers</li>
              </ul>
            </GlowCard>
          </div>
        </div>
      </section> */}

      <section className="py-24 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-slate-400 text-base leading-relaxed mb-10">
            AviaGov is not software that manages safety.
            AviaGov is infrastructure that governs how safety becomes auditable truth.
          </p>
          <Link
            to="/contact"
            className="inline-flex px-10 py-4 bg-accent text-primary font-bold rounded-xl"
            onClick={() => trackEvent('generate_lead', { form_id: 'safety_governance_cta_discuss', module: 'safety' })}
          >
            Discuss National Safety Governance Architecture
          </Link>
        </div>
      </section>
    </div>
  );
};

export default SafetyGovernance;
